import React from 'react';
import './index.scss';
import { Loader, Dimmer } from 'semantic-ui/semantic-ui-imports';
import { useTranslation } from 'react-i18next';
import { Gb, Dk, De, Fr, Es } from 'react-flags-select';
import AuthContext from 'contexts/auth/context';
import Select, { SingleValue, components } from 'react-select';

import { Language } from 'Types/Language';
import { User } from 'Types/User';
import { updateUser } from 'services/user';
import axios from '../../services/request';

type LanguageOption = {
  label: string;
  value: string;
};

const LanguageSelector = () => {
  const { user } = React.useContext(AuthContext);
  const [selectedLanguage, setSelectedLanguage] = React.useState<Language>();
  const { i18n } = useTranslation();
  const [languages, setLanguages] = React.useState<Language[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  const setLanguage = (code: string) => {
    const c = (code || 'en-gb').toLowerCase();
    i18n.changeLanguage(c);
    localStorage.setItem('i18nextLng', c);
    setSelectedLanguage(
      languages?.find((x: Language) => x.language.toLowerCase() === c)
    );
  };

  const getLanguageAccordingToUser = () => {
    let c = (i18n.language || 'en-gb').toLowerCase();
    if (user?.preferred_language && user?.preferred_language !== c) {
      c = user.preferred_language.toLowerCase();
    }
    return c;
  };

  const fetchLanguages = async () => {
    try {
      const { data } = await axios.get(`/languages/`);
      setLanguages(data);
    } catch (error) {
      // console.error(error);
    }
  };

  React.useEffect(() => {
    fetchLanguages();
  }, []);

  React.useEffect(() => {
    const code = getLanguageAccordingToUser();
    setLanguage(code);
  }, [user, languages]);

  const getFlag = (code: string) => {
    // eslint-disable-next-line no-param-reassign
    code = code.toLowerCase();
    if (code === 'en-gb') return <Gb />;
    if (code === 'da') return <Dk />;
    if (code === 'de') return <De />;
    if (code === 'fr') return <Fr />;
    if (code === 'fr') return <Fr />;
    if (code === 'es') return <Es />;
    return <Gb />;
  };

  const handleSelect = async (code: string) => {
    setLanguage(code);
    const language = languages?.find((x: Language) => x.language === code);
    setSelectedLanguage(language);
    // update setting to user
    if (user) {
      const newUser: User = { ...user, preferred_language: code };
      await updateUser(newUser.id, newUser);
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  React.useEffect(() => {
    if (user?.preferred_language) {
      const language = languages?.find(
        (x: Language) => x.language === user?.preferred_language
      );
      setSelectedLanguage(language);
    }
  }, [user]);

  const DropdownIndicator = (props: any) => (
    <components.DropdownIndicator {...props}>
      {!!selectedLanguage && getFlag(selectedLanguage?.language)}
    </components.DropdownIndicator>
  );

  return (
    <div className="language-selector">
      <Select<LanguageOption, false>
        options={languages.map((l) => ({ label: l.name, value: l.language }))}
        defaultValue={
          selectedLanguage
            ? {
                label: selectedLanguage?.language,
                value: selectedLanguage?.name,
              }
            : undefined
        }
        onChange={(val: SingleValue<LanguageOption>) => {
          handleSelect(val?.value || '');
        }}
        isSearchable={false}
        placeholder={false}
        name={'language'}
        components={{ DropdownIndicator }}
      />
      <Dimmer active={loading} page>
        <Loader />
      </Dimmer>
    </div>
  );
};

LanguageSelector.defaultProps = {};

export default LanguageSelector;
